/**
 * Shared presentational primitives for the 022 chat-thread widgets.
 *
 * Ported 1:1 from the Anaka design handoff
 * (`backlog/specs/022-chat-thread-widgets/design/screens/chat-widgets.jsx`
 * + `home-lifecycle.jsx`). The handoff cards are the `.glass` surface
 * (== the app's `.glass-card`), with inline-style internals. Here those
 * internals are translated to Tailwind arbitrary-value classes + semantic
 * CSS-var classes, following the established port pattern in
 * `src/components/v2/participants/IdealCustomerProfileCard.tsx`.
 *
 * Colour rules (research.md Decision 1–2):
 *  - Confidence tiers use `--color-green-500 | --color-orange-500 | --color-red-500`.
 *  - Brand accents use the `--color-purple-400 | --color-blue-400 | …` scale.
 *  - Numerics use `font-mono` (Fira Code).
 */
import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus, Mic, Activity, ArrowUpRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { WidgetAction } from '@shared/conversation-schema';
import { cn } from '@/lib/utils';
import { resolveWidgetIcon } from './widget-icons';
import { capitalizeFirst } from './textFormat';

const ACCENT_PURPLE = 'var(--color-purple-400)';
const ACCENT_BLUE = 'var(--color-blue-400)';

/**
 * Optional display metadata forwarded alongside a widget action click so the
 * consumer can resolve the user-turn text: the action's synthetic `text`, then
 * its button `label`. (The action `payload` still takes precedence — see
 * `ChatThread.handleTranscriptAction`.)
 */
export type WidgetActionMeta = { text?: string; label?: string };

/** Render-prop shape for widgets that surface `WidgetBlock.actions` in-card. */
export interface WidgetActionProps {
  actions?: WidgetAction[];
  onAction?: (action: string, payload?: Record<string, unknown>, meta?: WidgetActionMeta) => void;
}

export type ConfidenceLvl = 'hi' | 'mid' | 'lo';
export type TrendDir = 'up' | 'down' | 'flat';

/** Inter Display for titles, with a graceful fallback. */
export const DISPLAY_FONT = 'var(--font-display, Inter, sans-serif)';

/** Confidence-tier foreground colour (theme-stable CSS vars). */
export function tierColor(lvl: ConfidenceLvl): string {
  if (lvl === 'hi') return 'var(--color-green-500)';
  if (lvl === 'mid') return 'var(--color-orange-500)';
  return 'var(--color-red-500)';
}

/** `color-mix` helper — translucent tint of any colour/CSS-var. */
export function mix(color: string, pct: number): string {
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
}

/**
 * Thread-width glass card shell. Matches the handoff `.glass` (radius 16,
 * `--card-border`, soft purple shadow, translucent blur) via the app's
 * `.glass-card`, and caps at the assistant column width (560px) so widgets
 * size to the thread, never intrinsic/centred.
 */
export function ChatCard({
  className,
  children,
  ...rest
}: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('glass-card w-full max-w-[min(560px,100%)]', className)} {...rest}>
      {children}
    </div>
  );
}

/** Confidence pill — mono %, tier-tinted (`.conf` in the handoff). */
export function ConfidencePill({
  lvl,
  children,
  className,
}: {
  lvl: ConfidenceLvl;
  children: ReactNode;
  className?: string;
}) {
  const color = tierColor(lvl);
  return (
    <span
      className={cn(
        'inline-flex min-w-[42px] items-center justify-center rounded-md px-2 py-[3px] font-mono text-[11px] font-bold',
        className,
      )}
      style={{ background: mix(color, 15), color }}
    >
      {children}
    </span>
  );
}

/**
 * Signed trend delta — arrow + `±N%`, green up / red down (`.trend`).
 * `trend === 0` renders nothing (matches the handoff `{delta && …}` guard).
 */
export function TrendDelta({ trend, className }: { trend: number; className?: string }) {
  if (!trend) return null;
  const up = trend > 0;
  const color = up ? 'var(--color-green-500)' : 'var(--color-red-500)';
  const Arrow = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn('inline-flex items-center gap-[3px] font-mono text-[11px] font-semibold', className)}
      style={{ color }}
    >
      <Arrow className="size-[11px]" aria-hidden />
      {up ? '+' : '−'}
      {Math.abs(trend)}%
    </span>
  );
}

/** Directional trend arrow for list rows (`up | down | flat`). */
export function TrendArrow({ dir }: { dir: TrendDir }) {
  if (dir === 'up')
    return <TrendingUp className="size-[11px]" style={{ color: 'var(--color-green-500)' }} aria-hidden />;
  if (dir === 'down')
    return <TrendingDown className="size-[11px]" style={{ color: 'var(--color-red-500)' }} aria-hidden />;
  return <Minus className="size-[11px] text-muted-foreground" aria-hidden />;
}

/** Count chip — icon + value, neutral fill with a coloured glyph/text. */
export function CountPill({
  icon: Icon,
  color,
  children,
}: {
  icon: LucideIcon;
  color: string;
  children: ReactNode;
}) {
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary px-2 py-[3px] font-mono text-[10px] font-semibold"
      style={{ color }}
    >
      <Icon className="size-[11px]" aria-hidden />
      {children}
    </span>
  );
}

/**
 * Tiny confidence sparkline (ported from the handoff `TrendSpark`).
 * Normalises `points` into a 116×30 path; colour follows `up`.
 */
export function TrendSpark({
  points,
  up = true,
  w = 116,
  h = 30,
}: {
  points: number[];
  up?: boolean;
  w?: number;
  h?: number;
}) {
  if (!points || points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const stepX = w / (points.length - 1);
  const xy = points.map((p, i) => [i * stepX, h - ((p - min) / span) * (h - 6) - 3] as const);
  const d = xy.map((c, i) => (i ? 'L' : 'M') + c[0].toFixed(1) + ' ' + c[1].toFixed(1)).join(' ');
  const col = up ? 'var(--color-green-500)' : 'var(--color-red-500)';
  const last = xy[xy.length - 1];
  return (
    <svg width={w} height={h} className="block shrink-0 overflow-visible" aria-hidden>
      <path d={d} fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={col} />
    </svg>
  );
}

/**
 * Deterministic fallback sparkline when a widget ships no `sparkline` data —
 * a gentle series ending at `confidence`, sloped by `trend`. No RNG so the
 * render is stable (and SSR/test-safe).
 */
export function deriveSpark(confidence: number, trend: number): number[] {
  const n = 7;
  const start = Math.max(2, Math.min(98, confidence - (trend || 4) - 6));
  const pts: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const base = start + (confidence - start) * t;
    pts.push(Math.max(0, base + Math.sin(i * 1.3) * 3));
  }
  pts[n - 1] = confidence;
  return pts;
}

/**
 * Snapshot header — icon tile · eyebrow · title, with an optional
 * confidence pill + trend delta stacked on the right (`SnapHead`).
 */
export function SnapHead({
  icon: Icon,
  iconColor,
  eyebrow,
  title,
  confidence,
  lvl,
  trend,
}: {
  icon: LucideIcon;
  iconColor: string;
  eyebrow: string;
  title: string;
  confidence?: number;
  lvl?: ConfidenceLvl;
  trend?: number;
}) {
  return (
    <div className="flex items-start gap-[11px]">
      <span
        className="inline-flex size-[30px] shrink-0 items-center justify-center rounded-lg"
        style={{ background: mix(iconColor, 14), border: `1px solid ${mix(iconColor, 28)}`, color: iconColor }}
      >
        <Icon className="size-[15px]" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span
          className="font-mono text-[9.5px] font-bold uppercase tracking-[0.08em]"
          style={{ color: iconColor }}
        >
          {eyebrow}
        </span>
        <span
          className="text-[14.5px] font-bold leading-[1.3] tracking-[-0.005em] text-foreground"
          style={{ fontFamily: DISPLAY_FONT }}
        >
          {capitalizeFirst(title)}
        </span>
      </div>
      {confidence != null && lvl && (
        <div className="flex shrink-0 flex-col items-end gap-1">
          <ConfidencePill lvl={lvl}>{confidence}%</ConfidencePill>
          {trend != null && <TrendDelta trend={trend} />}
        </div>
      )}
    </div>
  );
}

/**
 * Standardised spark/stat well shared by BetSnapshot + AssumptionSnapshot:
 * the confidence trendline on the left, interviews + signals as labelled stat
 * rows, and a right-aligned icon-only link out to the (parent) bet — the
 * "view evidence / open bet" affordance now lives inside this well.
 */
export function SparkStatsWell({
  spark,
  up,
  interviews,
  signals,
  navTo = 'bets',
  linkLabel = 'Open bet',
  onOpen,
}: {
  spark: number[];
  up?: boolean;
  interviews?: number;
  signals?: number;
  navTo?: string;
  linkLabel?: string;
  onOpen?: () => void;
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-[11px] border border-border px-3 py-2.5"
      style={{ background: 'var(--tinted-bg)' }}
    >
      <TrendSpark points={spark} up={up} />
      {(interviews != null || signals != null) && (
        <div className="flex flex-1 items-center justify-center gap-5">
          {interviews != null && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-foreground">
              <Mic className="size-[13px]" style={{ color: ACCENT_PURPLE }} aria-hidden />
              <strong className="font-mono font-bold">{interviews}</strong> interviews
            </span>
          )}
          {signals != null && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-foreground">
              <Activity className="size-[13px]" style={{ color: ACCENT_BLUE }} aria-hidden />
              <strong className="font-mono font-bold">{signals}</strong> signals
            </span>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        data-nav-to={navTo}
        aria-label={linkLabel}
        className="ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-secondary text-muted-foreground transition-colors hover:border-[var(--border-strong)] hover:text-foreground"
      >
        <ArrowUpRight className="size-[14px]" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Hero card shell — the `HeroCard` from the home-lifecycle handoff, adapted to
 * thread width. `variant='default'` is the brand-purple treatment (used for
 * "go" decisions); `variant='action'` is the accent-tinted "needs you" chrome
 * (`accent` sets the hue). Renders an icon tile · optional kicker · title ·
 * desc, then `children` (progress / recap), then optional right-aligned actions.
 */
export function HeroShell({
  variant = 'action',
  accent = 'var(--color-orange-500)',
  icon,
  kicker,
  title,
  desc,
  badge,
  children,
  actions,
}: {
  variant?: 'default' | 'action';
  accent?: string;
  icon: ReactNode;
  kicker?: string;
  title: string;
  desc?: string;
  /** Optional top-right slot (e.g. a draft/confirmed state pill). */
  badge?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const isAction = variant === 'action';
  const cardStyle: React.CSSProperties = isAction
    ? {
        borderColor: mix(accent, 42),
        background: `linear-gradient(165deg, ${mix(accent, 11)}, transparent 58%), var(--background-glass)`,
        boxShadow: `0 16px 52px ${mix(accent, 20)}, 0 4px 16px rgba(0,0,0,0.22), inset 0 0 0 1px ${mix(accent, 16)}`,
      }
    : {
        borderColor: 'rgba(124,58,237,0.32)',
        boxShadow:
          '0 16px 52px rgba(124,58,237,0.20), 0 4px 16px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(124,58,237,0.10)',
      };

  return (
    <div
      className="glass-card flex w-full max-w-[min(560px,100%)] flex-col gap-[18px] px-[26px] py-6 text-sm"
      style={cardStyle}
    >
      <div className="flex items-start gap-4">
        <span
          className="inline-flex size-12 shrink-0 items-center justify-center rounded-[14px]"
          style={
            isAction
              ? { background: mix(accent, 16), border: `1px solid ${mix(accent, 38)}`, color: accent }
              : { background: 'var(--gradient-primary)', boxShadow: '0 6px 18px rgba(124,58,237,0.35)', color: '#fff' }
          }
        >
          {icon}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {kicker && (
            <span
              className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em]"
              style={{ color: isAction ? accent : 'var(--color-purple-400)' }}
            >
              {kicker}
            </span>
          )}
          <h2
            className="m-0 text-[21px] font-bold leading-[1.25] tracking-[-0.01em] text-foreground"
            style={{ fontFamily: DISPLAY_FONT }}
          >
            {capitalizeFirst(title)}
          </h2>
          {desc && <p className="m-0 text-[13.5px] leading-[1.6] text-muted-foreground text-pretty">{capitalizeFirst(desc)}</p>}
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
      </div>
      {children}
      {actions && <div className="flex flex-wrap items-center justify-end gap-2.5">{actions}</div>}
    </div>
  );
}

/** Lucide icon glyph for the HeroShell tile (resolved from the widget's icon name). */
export function HeroIcon({ name, fallback }: { name?: string; fallback: LucideIcon }) {
  const Icon = resolveWidgetIcon(name, fallback);
  return <Icon className="size-[22px]" strokeWidth={1.8} data-icon={name} aria-hidden />;
}

/**
 * In-card action buttons for the hero cards (`lcPrimaryBtn` / `lcGhostBtn`).
 * `primary` actions get the brand gradient; everything else is the ghost
 * treatment. Right-aligned by the HeroShell `actions` row.
 */
export function HeroActionButtons({ actions, onAction }: WidgetActionProps) {
  if (!actions || actions.length === 0) return null;
  return (
    <>
      {actions.map((a) => {
        const primary = a.variant === 'primary';
        return (
          <button
            key={a.action}
            type="button"
            onClick={() =>
              onAction?.(a.action, a.payload as Record<string, unknown> | undefined, {
                text: a.text,
                label: a.label,
              })
            }
            className={cn(
              'inline-flex items-center gap-2 whitespace-nowrap rounded-[11px] text-[13.5px] font-semibold',
              primary
                ? 'px-5 py-[11px] text-white'
                : 'border border-border bg-secondary px-4 py-[11px] text-foreground transition-colors hover:border-[var(--border-strong)]',
            )}
            style={primary ? { background: 'var(--gradient-primary)', boxShadow: '0 6px 18px rgba(124,58,237,0.32)' } : undefined}
          >
            {a.label}
          </button>
        );
      })}
    </>
  );
}

/**
 * Ghost "open / view" link button used at the foot of snapshot cards
 * (`.btn .btn-ghost` in the handoff). Right chevron / arrow supplied by caller.
 */
export function GhostLinkButton({
  children,
  onClick,
  className,
  ...rest
}: { children: ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 self-start rounded-[9px] border border-border bg-transparent px-3 py-1.5',
        'font-sans text-[12px] font-medium text-muted-foreground transition-colors',
        'hover:border-[var(--border-strong)] hover:text-foreground',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
